import React from "react";

/**
 * Safely parse JSON if it's a string, otherwise pass through.
 */
function parseMaybeJson(v) {
  if (!v) return null;
  if (typeof v === "string") {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
}

/**
 * Compute CSS for a circular avatar from image_json (people mode).
 * Uses 'avatarCircle' crop: { center:{x,y}, radius } + original {width,height}.
 * Returns container (square, masked to circle) + img styles aligned to the admin modal.
 */
function getAvatarCropStyles(imageJson, presetKey = "avatarCircle", sizePx = 64) {
  const j = parseMaybeJson(imageJson);
  if (!j || typeof j !== "object") return null;

  const iw = Number(j?.original?.width);
  const ih = Number(j?.original?.height);
  if (!iw || !ih) return null;

  const c = j?.crops?.[presetKey];
  if (!c || c.shape !== "circle" || !c.center || typeof c.radius !== "number") return null;

  const cx = Number(c.center.x);
  const cy = Number(c.center.y);
  const r = Number(c.radius);
  if (!isFinite(cx) || !isFinite(cy) || !isFinite(r) || r <= 0) return null;

  // side (px) of the square that bounds the circle in the image
  const sidePx = 2 * r * Math.min(iw, ih);

  // normalized bounding square for the circle
  const xNorm = (cx * iw - sidePx / 2) / iw;
  const yNorm = (cy * ih - sidePx / 2) / ih;
  const wNorm = sidePx / iw;
  const hNorm = sidePx / ih;
  if (wNorm <= 0 || hNorm <= 0) return null;

  // container is a fixed square with circular mask
  const container = {
    position: "relative",
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    borderRadius: "50%",
    overflow: "hidden",
    background: "#f3f3f3",
    flex: "0 0 auto",
  };

  // Image is scaled so that the bounding square fills the container,
  // then offset so the square aligns inside the container.
  const img = {
    position: "absolute",
    display: "block",
    width: `${(1 / wNorm) * 100}%`,
    height: "auto",
    left: `-${(xNorm / wNorm) * 100}%`,
    top: `-${(yNorm / hNorm) * 100}%`,
    maxWidth: "none",
    userSelect: "none",
    pointerEvents: "none",
  };

  return { container, img };
}

const RecommendationCard = ({ quote, name, title, image, imageJson }) => {
  const styles = getAvatarCropStyles(imageJson, "avatarCircle", 64);
  const hasCrop = !!styles;

  return (
    <div className="rec-item">
      <div className="rec-person">
        <div
          className={`avatar ${hasCrop ? "has-crop" : "no-crop"}`}
          style={styles?.container}
        >
          {image && (
            <img
              src={image}
              alt={name}
              className={`avatar__img ${hasCrop ? "has-crop" : "no-crop"}`}
              style={styles?.img}
            />
          )}
        </div>
        <div className="person-info">
          <div className="name">{name}</div>
          <div className="title">{title}</div>
        </div>
      </div>
      <div className="rec-quote">
        <blockquote>"{quote}"</blockquote>
      </div>
    </div>
  );
};

export default RecommendationCard;
